import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PageHeader, Button, Badge } from "@/components/ui/PageHeader";
import * as conv from "@/api/conversations";

// ═══════════════════════════════════════════════════════════════════
// Messages — the inbox after a mutual wave.
//
// Three things worth seeing here:
//
//   · Each conversation carries its LAST MESSAGE, so the inbox says what was
//     said, not only who and when. Null until somebody writes; a removed
//     message keeps its place without its words.
//   · The unread count is the ONE call an app polls until push arrives. It
//     also returns `lastMessageAt`, and the page refetches the list and the
//     open conversation only when that time moves. Turn polling on and write
//     from the other side to watch it.
//   · Opening a conversation marks your side read. Replying does too, because
//     the sender has read what they answered. Your own read does not move
//     `lastMessageAt`, so the page updates its inbox itself after opening one.
//
// To walk both sides with the two test members, use the Test member page:
// reset `peer` to discoverable, then `first-run` to waved-at, wave back on
// the Waves page, and open the conversation here.
// ═══════════════════════════════════════════════════════════════════

/** The app's budget while a conversation is on screen. */
const POLL_MS = 10_000;

function lineFor(m: conv.ConversationMessage | null): string {
  if (!m) return "No messages yet.";
  if (m.isRedacted) return m.isMine ? "You: (removed)" : "(removed)";
  return m.isMine ? `You: ${m.body}` : (m.body ?? "");
}

function timeOf(iso: string | null | undefined): string {
  return iso ? new Date(iso).toLocaleTimeString() : "—";
}

export default function ConversationsLab() {
  const qc = useQueryClient();
  const [openId, setOpenId] = useState<string | null>(null);
  const [peerUserId, setPeerUserId] = useState("");
  const [draft, setDraft] = useState("");
  // The last send, so it can be retried with the SAME key (one message, not two).
  const [lastSend, setLastSend] = useState<{ body: string; key: string; id: string } | null>(null);
  const [polling, setPolling] = useState(false);
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState<string[]>([]);
  const lastSeen = useRef<string | null | undefined>(undefined);

  const say = (m: string) => setLog((l) => [`${new Date().toLocaleTimeString()}  ${m}`, ...l].slice(0, 10));

  const unread = useQuery({
    queryKey: ["conv-unread"],
    queryFn: conv.getConversationsUnreadCount,
    refetchInterval: polling ? POLL_MS : false,
  });

  const list = useQuery({
    queryKey: ["conv-list"],
    queryFn: () => conv.listConversations({ limit: 20 }),
  });

  const thread = useQuery({
    queryKey: ["conv-thread", openId],
    queryFn: () => conv.getConversation(openId as string, { limit: 30 }),
    enabled: !!openId,
  });

  // The cheap check: refetch the list and the open conversation only when
  // the newest message time moves.
  const latest = unread.data?.data.lastMessageAt;
  useEffect(() => {
    if (latest === undefined) return;
    if (lastSeen.current !== undefined && lastSeen.current !== latest) {
      say(`lastMessageAt moved to ${timeOf(latest)}: refetching the list and the open conversation`);
      void qc.invalidateQueries({ queryKey: ["conv-list"] });
      void qc.invalidateQueries({ queryKey: ["conv-thread"] });
    }
    lastSeen.current = latest;
  }, [latest, qc]);

  // Opening a conversation marks your side read. That is your own change, so
  // the check does not report it: update the inbox and the badge yourself.
  const readAt = thread.dataUpdatedAt;
  useEffect(() => {
    if (!readAt) return;
    void qc.invalidateQueries({ queryKey: ["conv-list"] });
    void qc.invalidateQueries({ queryKey: ["conv-unread"] });
  }, [readAt, qc]);

  const run = async (fn: () => Promise<string>) => {
    setBusy(true);
    try {
      say(await fn());
      await qc.invalidateQueries({ queryKey: ["conv-list"] });
      await qc.invalidateQueries({ queryKey: ["conv-unread"] });
      await qc.invalidateQueries({ queryKey: ["conv-thread"] });
    } catch (e) {
      say("✘ " + (e instanceof Error ? e.message : "failed"));
    } finally {
      setBusy(false);
    }
  };

  const conversations = list.data?.data.conversations ?? [];
  const open = conversations.find((c) => c.id === openId) ?? thread.data?.data.conversation ?? null;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Messages"
        subtitle="The conversations of people who waved at each other — and the one check an app polls."
        breadcrumbs={[
          { label: "Dashboard", href: "/" },
          { label: "Identity Board", href: "/lab/identity-board" },
          { label: "Messages" },
        ]}
      />

      <section className="bg-white border border-gray-200 rounded-2xl p-5 space-y-3">
        <div className="flex items-center gap-3 flex-wrap">
          <h2 className="text-sm font-medium text-gray-900">GET /conversations/unread-count</h2>
          {unread.data && (
            <Badge variant={unread.data.data.unreadCount > 0 ? "green" : "gray"}>
              {unread.data.data.unreadCount} unread
            </Badge>
          )}
          <span className="text-xs text-gray-600">
            lastMessageAt: {timeOf(unread.data?.data.lastMessageAt)}
          </span>
          {/* A pull. Polling pauses while the page is hidden, as an app's should. */}
          <Button size="sm" variant="secondary" onClick={() => void unread.refetch()}>
            Check now
          </Button>
          <label className="flex items-center gap-2 text-xs text-gray-600 ml-auto">
            <input type="checkbox" checked={polling} onChange={(e) => setPolling(e.target.checked)} />
            Poll every {POLL_MS / 1000} s, as the app does with a conversation open
          </label>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <input
            value={peerUserId}
            onChange={(e) => setPeerUserId(e.target.value)}
            placeholder="Open with a mutual: their user id"
            className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm flex-1 min-w-60"
          />
          <Button
            size="sm"
            isLoading={busy}
            disabled={!peerUserId.trim()}
            onClick={() =>
              run(async () => {
                const res = await conv.openConversation(peerUserId.trim());
                setOpenId(res.data.conversation.id);
                return res.data.resumed ? "Resumed the conversation." : "Opened a new conversation.";
              })
            }
          >
            Open
          </Button>
        </div>
      </section>

      <section className="bg-white border border-gray-200 rounded-2xl p-5">
        <h2 className="text-sm font-medium text-gray-900 mb-3">Inbox</h2>
        {list.isLoading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : list.error ? (
          <p className="text-sm text-red-700">
            {list.error instanceof Error ? list.error.message : "Could not load"}
          </p>
        ) : !conversations.length ? (
          <p className="text-sm text-gray-500">
            No conversations. One starts after a mutual wave, with "Say namaste".
          </p>
        ) : (
          <ul className="space-y-2">
            {conversations.map((c) => (
              <li
                key={c.id}
                className={`border rounded-xl px-4 py-3 text-sm ${
                  c.unreadCount > 0 ? "border-gray-900 bg-gray-50" : "border-gray-200"
                }`}
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-gray-900">{c.peer.name}</span>
                  {c.peer.city && <span className="text-xs text-gray-500">{c.peer.city}</span>}
                  {c.unreadCount > 0 && <Badge variant="green">{c.unreadCount} unread</Badge>}
                  <Badge variant={c.status === "open" ? "blue" : "gray"}>{c.status}</Badge>
                  <span className="text-xs text-gray-400">{timeOf(c.lastMessageAt)}</span>
                  <button
                    onClick={() => setOpenId(openId === c.id ? null : c.id)}
                    className="ml-auto text-xs text-blue-600 hover:underline"
                  >
                    {openId === c.id ? "close" : "open"}
                  </button>
                </div>
                <p className="text-xs text-gray-600 mt-1">{lineFor(c.lastMessage)}</p>
              </li>
            ))}
          </ul>
        )}
      </section>

      {openId && (
        <section className="bg-white border border-gray-200 rounded-2xl p-5 space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-sm font-medium text-gray-900">
              {open ? `With ${open.peer.name}` : "Conversation"}
            </h2>
            {open && <Badge variant={open.status === "open" ? "blue" : "gray"}>{open.status}</Badge>}
            <span className="text-xs text-gray-500">Newest first. Opening it marked your side read.</span>
          </div>
          {thread.isLoading ? (
            <p className="text-xs text-gray-500">Loading messages…</p>
          ) : thread.error ? (
            <p className="text-xs text-red-700">
              {thread.error instanceof Error ? thread.error.message : "Could not load"}
            </p>
          ) : (
            <ul className="space-y-2">
              {(thread.data?.data.messages ?? []).map((m) => (
                <li
                  key={m.id}
                  className={`text-xs rounded-lg px-3 py-2 ${
                    m.isMine ? "bg-blue-50 border border-blue-100" : "bg-gray-50 border border-gray-100"
                  }`}
                >
                  <span className="font-medium">{m.isMine ? "You" : open?.peer.name}</span>{" "}
                  <span className="text-gray-400">{timeOf(m.createdAt)}</span>
                  <div className="text-gray-700 mt-0.5">{m.isRedacted ? "(removed)" : m.body}</div>
                  {!m.isMine && !m.isRedacted && (
                    <button
                      className="text-red-600 hover:underline mt-1"
                      onClick={() =>
                        run(async () => {
                          await conv.reportConversationMessage(m.id, "other", "lab test report");
                          return "Reported into the moderation queue.";
                        })
                      }
                    >
                      report
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}

          <div className="flex gap-2 flex-wrap items-center">
            <input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Write a message…"
              className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm flex-1 min-w-40"
            />
            <Button
              size="sm"
              isLoading={busy}
              disabled={!draft.trim()}
              onClick={() =>
                run(async () => {
                  const key = crypto.randomUUID();
                  const res = await conv.sendConversationMessage(openId, draft.trim(), key);
                  setLastSend({ body: draft.trim(), key, id: res.data.id });
                  setDraft("");
                  return "Sent.";
                })
              }
            >
              Send
            </Button>
            {lastSend && (
              <Button
                size="sm"
                variant="secondary"
                isLoading={busy}
                onClick={() =>
                  run(async () => {
                    const res = await conv.sendConversationMessage(openId, lastSend.body, lastSend.key);
                    return res.data.id === lastSend.id
                      ? "Same key, same message: nothing new was sent."
                      : "A second message was sent. That is the bug the key exists to stop.";
                  })
                }
              >
                Retry last send with its key
              </Button>
            )}
            <Button
              size="sm"
              variant="secondary"
              isLoading={busy}
              onClick={() =>
                run(async () => {
                  await conv.markConversationRead(openId);
                  return "Marked read.";
                })
              }
            >
              Mark read
            </Button>
            <Button
              size="sm"
              variant="secondary"
              isLoading={busy}
              onClick={() =>
                run(async () => {
                  await conv.closeConversation(openId);
                  return "Ended. New messages are refused; the transcript stays.";
                })
              }
            >
              End
            </Button>
          </div>
        </section>
      )}

      {log.length > 0 && (
        <section className="bg-gray-50 border border-gray-200 rounded-2xl p-4">
          <ul className="space-y-1 font-mono text-xs text-gray-700">
            {log.map((line, i) => (
              <li key={i}>{line}</li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
